'use client';

import React, { useEffect, useState } from 'react';
import type { ContentType as TContentType } from '@jlg-website/database/types';
import { ContentType } from '@jlg-website/shared/constants';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { formComponents } from './form-components';

export function ContentDialogForm({
  onCreateAction,
  contentTypeName,
}: {
  onCreateAction: () => void;
  contentTypeName: (typeof ContentType)[keyof typeof ContentType];
}) {
  const [open, setOpen] = useState(false);
  const [contentType, setContentType] = useState<TContentType | null>(null);

  const handleSave = () => {
    setOpen(false);
    onCreateAction();
  };

  useEffect(() => {
    if (!open) return;

    const fetchContentType = async () => {
      const request = await fetch(`/admin/api/content-type?name=${contentTypeName}`, {
        cache: 'no-store',
      });
      const data = await request.json();
      setContentType(data);
    };

    fetchContentType();
  }, [open]);

  const FormComponent = formComponents[contentTypeName] as React.ComponentType<{
    contentType: TContentType;
    onSave: () => void;
    customCancelButton: React.ReactNode;
  }> | null;

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) setContentType(null);
      }}
    >
      <DialogTrigger asChild>
        <Button className="w-full">
          <Plus /> <strong>Create {contentTypeName}</strong>
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl gap-0 p-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-0">
          <DialogTitle>Create content</DialogTitle>
        </DialogHeader>
        {contentType && FormComponent && (
          <div className="flex-1 overflow-y-auto px-6 pt-4 pb-6">
            <FormComponent
              contentType={contentType}
              onSave={handleSave}
              customCancelButton={
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
              }
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
